import React, { useState, useEffect, useCallback } from 'react';
import { format, startOfWeek, addDays, getWeek, isSameWeek } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import AvailabilityGrid from '../components/availability/AvailabilityGrid';
import AvailabilityStatusChip from '../components/availability/AvailabilityStatusChip';

const Availability = () => {
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [saveStatus, setSaveStatus] = useState({
    isSaving: false,
    lastSaved: null,
    hasUnsavedChanges: false,
  });

  const navigateWeek = useCallback(
    (direction) => {
      const newWeek = addDays(currentWeek, direction * 7);
      setCurrentWeek(newWeek);
    },
    [currentWeek]
  );

  const handleSaveStatusChange = useCallback((status) => {
    setSaveStatus(status);
  }, []);

  const getWeekDisplayText = (weekStart) => {
    const today = new Date();
    const thisWeekStart = startOfWeek(today, { weekStartsOn: 1 });
    const nextWeekStart = addDays(thisWeekStart, 7);
    const weekNumber = getWeek(weekStart, { weekStartsOn: 1 });

    if (isSameWeek(weekStart, thisWeekStart, { weekStartsOn: 1 })) {
      return `this week (${weekNumber})`;
    } else if (isSameWeek(weekStart, nextWeekStart, { weekStartsOn: 1 })) {
      return `next week (${weekNumber})`;
    } else {
      return `week ${weekNumber}`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Week navigation header - not sticky */}
      <div className="border-b border-gray-200 bg-gray-50 pb-4 pt-2">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Availability</h1>
              <p className="mt-1 text-sm text-gray-500"></p>
            </div>

            {/* Status chip moved to left side next to title */}
            <AvailabilityStatusChip
              isSaving={saveStatus.isSaving}
              lastSaved={saveStatus.lastSaved}
              hasUnsavedChanges={saveStatus.hasUnsavedChanges}
            />
          </div>

          {/* Week Navigation - responsive design */}
          <div className="mt-4 flex items-center justify-center space-x-2 lg:mt-0 lg:justify-end">
            <button
              onClick={() => navigateWeek(-1)}
              className="btn-secondary flex-shrink-0 touch-manipulation p-2"
              aria-label="Previous week"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>

            <div className="min-w-0 rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm">
              <span className="block text-center text-sm font-medium text-gray-900">
                {getWeekDisplayText(currentWeek)}
              </span>
            </div>

            <button
              onClick={() => navigateWeek(1)}
              className="btn-secondary flex-shrink-0 touch-manipulation p-2"
              aria-label="Next week"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <AvailabilityGrid
        currentWeek={currentWeek}
        onWeekChange={navigateWeek}
        onSaveStatusChange={handleSaveStatusChange}
      />
    </div>
  );
};

export default Availability;
